// src/views/FileManager/components/VFSBrowser.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileBrowser as FileBrowserWithoutTypes,
  ChonkyActions,
  ChonkyFileActionData,
  ChonkyIconName,
  FileBrowserProps,
  FileBrowserHandle,
  FileNavbar,
} from 'chonky';

import { CustomFileList } from './CustomFilelist';
import { FileGridView } from './FileGridView';
import { useCustomFileMap } from '../../../hooks/useCustomFileMap';
import { useFileActionHandler } from '../../../hooks/useFileActionHandler';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useFileDownload } from '../../../hooks/useFileDownload';
import { FileNode, NullableFileNode, CustomFileMap, FileManagerActions, DeleteSummary } from '../../../types/FileManagerTypes';
import { useLegendForm } from '../../../context/MapLegendContext';
import { useQuery } from '@tanstack/react-query';
import Notification from '../../../shared/components/Notification';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { FileAction } from '../../../enums/fileActions';
import { useAuth } from '../../../context/AuthContext';
import { AllowedRoles } from '../../../routes/routeConstants/allowedRoles';
import MapPortalService from '../../../services/MapPortal/mapportal.service';

import { UploadModal, ReuploadModal, FileDetailsModal, MoveModal, RestoreTrashModal, PreviewFileModal, FilePermissionsModal } from './Modals';
import UploadProgressModal from './UploadProgressModal';
import CustomWarning from '../../../shared/components/CustomWarning';
import { buildDeleteSummaryMessage, buildMapUsageMessage } from '../utils/deleteSummary';

import { Button, Spin, Dropdown } from 'antd';
import { AiFillCaretDown } from 'react-icons/ai';
import { SearchBar } from './SearchBar/SearchBar';
import { DUPLICATE_NAME_MESSAGE, SelectAllCheckbox } from './fileListShared';
import { FilterPanel } from './FilterPanel';
import { useLocationFilterStore } from '../../../hooks/useLocationFilterStore';
import { useFileSearchAndFilters } from '../../../hooks/useFileSearchAndFilters';
import { CheckboxFilterListOption } from '../../../shared/components/CheckboxFilterList';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import { SPATIAL_PREVIEW_EXTS, NO_PREVIEW_EXTS, fileActionAccess, ROOT_DISPLAY_NAME } from '../constants';
const FileBrowser = FileBrowserWithoutTypes as any;

// Strip tags AND decode entities (&nbsp;, &amp;, ...) via the DOM parser, then
// collapse whitespace (nbsp   included) so titles render cleanly.
const stripHtml = (html?: string) => {
  const text = new DOMParser().parseFromString(html ?? '', 'text/html').body.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
};

export type VFSProps = Partial<FileBrowserProps>;

export interface VFSBrowserProps extends VFSProps {
  customFileMap: CustomFileMap;
  setFolderLocation?: (location: string) => void;
  setFolderId?: (location: string) => void;
  fileManagerActionsRef?: React.MutableRefObject<FileManagerActions>;
  /** Notifies the parent when inline folder creation opens/closes (footer icon). */
  onCreatingFolderChange?: (creating: boolean) => void;
}

export const VFSBrowser: React.FC<VFSBrowserProps> = React.memo((props) => {
  const { fileManagerActionsRef } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [fileListViewType, setFileListViewType] = useState<'list' | 'tile'>('list');

  const [applyOverlay, setApplyOverlay] = useState<boolean>(false);
  const hideOverlay = () => setApplyOverlay(false);

  const [selectedReuploadFiles, setSelectedReuploadFiles] = useState<FileNode[]>();
  const [showReuploadModal, setShowReuploadModal] = useState<boolean>(false);
  const showReupload = (selectedFile?: FileNode[]) => {
    const files = selectedFile?.length ? selectedFile : chonkySelectedFiles;
    if (!files?.length) return;

    // Reupload targets the selected file's parent, not the folder that was last
    // used by the normal-upload modal. Without this, opening a nested folder
    // after a root upload leaves uploadFolderId at root and completion replaces
    // the wrong folder.
    const targetFolderId = files[0].parentId || currentFolderId || rootFolderId;
    setUploadFolderId(targetFolderId);

    setDragging(false);
    setSelectedReuploadFiles(files);
    setShowReuploadModal(true);
  };
  const hideReupload = () => {
    hideOverlay();
    setShowReuploadModal(false);
    setDragging(true);
  };

  const [showAddFilesModal, setShowAddFilesModal] = useState<boolean>(false);
  const showAddFiles = () => {
    setDragging(false);
    setShowAddFilesModal(true);
  };
  const hideAddFiles = () => {
    setDragging(false);
    hideOverlay();
    setShowAddFilesModal(false);
  };

  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  // Pre-filled default name for the inline temp-folder input, computed when the
  // add-folder flow starts so it avoids clashing with existing sibling folders.
  const [newFolderName, setNewFolderName] = useState<string>('New Folder');
  // In-flight guard for the create request: `submittingRef` prevents double
  // submits (double-Enter, and the blur that re-fires after a successful commit
  // unmounts the temp tile); `submittingFolder` drives the input's loading state.
  const submittingRef = useRef<boolean>(false);
  const [submittingFolder, setSubmittingFolder] = useState<boolean>(false);
  // Prevents double-Enter from firing two rename requests while one is in flight.
  const renamingRef = useRef<boolean>(false);

  const [selectedPreviewFile, setSelectedPreviewFile] = useState<FileNode>();
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const showPreview = (files?: FileNode[]) => {
    const target = (files && files[0]) || chonkySelectedFiles[0];
    if (!target || target.isDir) return;

    // Keep checkbox selection in sync with the row that was acted on
    setChonkySelectedFiles(files?.length ? files : [target]);
    setDragging(false);

    setSelectedPreviewFile(target);
    setShowPreviewModal(true);
  };
  const hidePreview = () => {
    setDragging(false);
    hideOverlay();
    setShowPreviewModal(false);
  };

  // Inline preview for standard (non-spatial) files -- distinct from the
  // FileDetailsModal state above, which stays open behind this one.
  const [previewFile, setPreviewFile] = useState<FileNode>();
  const [showFilePreview, setShowFilePreview] = useState<boolean>(false);
  const hideFilePreview = () => setShowFilePreview(false);

  const [selectedMoveFiles, setSelectedMoveFiles] = useState<FileNode[]>();
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);
  const showMove = (files?: FileNode[]) => {
    const targets = files?.length ? files : chonkySelectedFiles;
    if (!targets?.length) return;
    setDragging(false);
    setSelectedMoveFiles(targets);
    setShowMoveModal(true);
  };
  const hideMove = () => {
    setDragging(false);
    hideOverlay();
    setShowMoveModal(false);
  };

  const [selectedRestoreFiles, setSelectedRestoreFiles] = useState<FileNode[]>();
  const [showRestoreTrashModal, setShowRestoreTrashModal] = useState<boolean>(false);
  const showRestoreTrash = (files?: FileNode[]) => {
    const targets = files?.length ? files : chonkySelectedFiles;
    if (!targets?.length) return;
    setSelectedRestoreFiles(targets);
    setShowRestoreTrashModal(true);
  };
  const hideRestoreTrash = () => {
    hideOverlay();
    setShowRestoreTrashModal(false);
  };

  const [selectedDeleteFiles, setSelectedDeleteFiles] = useState<FileNode[]>();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const showDeleteConfirmation = (files: FileNode[] = chonkySelectedFiles) => {
    setSelectedDeleteFiles(files);
    setShowDeleteConfirmationModal(true);
  };
  const hideDeleteConfirmation = () => {
    hideOverlay();
    setShowDeleteConfirmationModal(false);
  };

  // Single-item only -- bulk permissions was dropped from the multiselect menu.
  const [selectedPermissionsFile, setSelectedPermissionsFile] = useState<FileNode>();
  const [showPermissionsModal, setShowPermissionsModal] = useState<boolean>(false);
  const showPermissions = (files?: FileNode[]) => {
    const target = (files?.length ? files : chonkySelectedFiles)?.[0];
    if (!target) return;
    setSelectedPermissionsFile(target);
    setShowPermissionsModal(true);
  };
  const hidePermissions = () => {
    hideOverlay();
    setShowPermissionsModal(false);
  };

  // Soft-delete (move to Trash) flow — counts and map usage come from the
  // server's delete summary, so nested content under un-expanded folders is
  // included (the previous in-memory fileMap walk could undercount it).
  const [trashIds, setTrashIds] = useState<string[]>([]);
  const [deleteSummary, setDeleteSummary] = useState<DeleteSummary | null>(null);
  const [showTrashSummaryModal, setShowTrashSummaryModal] = useState(false);
  const [showTrashMapLayerModal, setShowTrashMapLayerModal] = useState(false);

  const abortTrashFlow = useCallback(() => {
    setShowTrashSummaryModal(false);
    setShowTrashMapLayerModal(false);
    setTrashIds([]);
    setDeleteSummary(null);
    hideOverlay();
  }, []);

  const [chonkySelectedFiles, setChonkySelectedFiles] = useState<FileNode[]>([]);
  // Per-location filter memory, keyed by section pathname + folder id. Ref-backed,
  // so every entry is discarded when the user leaves the FileManager module.
  const filterStore = useLocationFilterStore();
  // Row currently in inline "insert name" edit mode -- set when the 3-dot
  // menu's Rename action fires, or after creating a new folder. Cleared on
  // commit (Enter / blur) inside the view's onCommitName callback.
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  // Persistent selection, lifted here so it survives the list<->tile toggle
  // and is shared by both views via useFileListSelection.
  const [selection, setSelection] = useState<string[]>([]);
  const fileBrowserRef = useRef<FileBrowserHandle>(null);
  const deepLinkAppliedRef = useRef(false);

  const { customFileMap, setFolderLocation, setFolderId } = props;

  const { canAccess } = useAuth();
  const canManageFiles = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });

  const {
    fileMap,
    rootFolderId,
    currentFolderId,
    filterFiles,
    setCurrentFolderId,
    uploadFolderId,
    setUploadFolderId,
    getFileLocation,
    deleteFiles,
    isDeleting,
    trashFiles,
    getDeleteSummary,
    isTrashing,
    restoreFiles,
    moveFiles,
    isMoving,
    createFolder,
    renameFile,
    updateMapping,
    isMappingLoading,
    updateLastAccessTime,
    createImageLayer,
    removeImageLayer,
  } = useCustomFileMap(customFileMap, true);

  // A layer's Data Source link opens this route in a new tab with the folder
  // encoded in the URL. Apply it once the initial file map is available.
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const requestedFolderId = searchParams.get('folderId') || (location.state as { openFolderId?: string } | null)?.openFolderId;
    if (!requestedFolderId || requestedFolderId === 'root') {
      deepLinkAppliedRef.current = true;
      return;
    }
    if (!fileMap[requestedFolderId]) return;

    deepLinkAppliedRef.current = true;
    setCurrentFolderId(requestedFolderId);
    setUploadFolderId(requestedFolderId);
  }, [fileMap, location.state, searchParams, setCurrentFolderId, setUploadFolderId]);

  // Search box, filter panel, and every listing fetch the two trigger.
  const {
    searchInput,
    setSearchInput,
    isSearchActive,
    searchLoading,
    draftSelection,
    isFilterActive,
    isFilterApplyLoading,
    filterDropdownOpen,
    onSelectSearchResult,
    handleApplyFilters,
    handleClearAllFilters,
    handleDraftSelectionChange,
    handleFilterDropdownOpenChange,
  } = useFileSearchAndFilters({ filterStore, currentFolderId, rootFolderId, setCurrentFolderId, filterFiles, updateMapping });

  // Ids the summary reported as inaccessible/missing are excluded from every
  // count it returned, so they must be excluded from the delete request too.
  const validTrashIds = useMemo(() => {
    if (!deleteSummary?.invalidItemIds.length) return trashIds;
    const invalid = new Set(deleteSummary.invalidItemIds);
    return trashIds.filter((id) => !invalid.has(id));
  }, [trashIds, deleteSummary]);

  const trashSummaryMessage = useMemo(() => (deleteSummary ? buildDeleteSummaryMessage(deleteSummary) : ''), [deleteSummary]);

  const trashMapLayerMessage = useMemo(() => (deleteSummary ? buildMapUsageMessage(deleteSummary) : ''), [deleteSummary]);

  // files always supplied by useFileActionHandler (single + multi entry points).
  // Fetches the summary first, so the confirmation modal opens with real counts;
  // the page Spin (isTrashing) covers the round trip.
  const showTrashConfirmation = useCallback(
    async (files: FileNode[] = []) => {
      const ids = (files ?? []).map((f) => f.id).filter(Boolean);
      if (ids.length === 0) return;

      const summary = await getDeleteSummary(ids);
      // null = request failed; getDeleteSummary already notified.
      if (!summary) return;

      if (summary.totalItemCount === 0) {
        Notification({
          message: "You don't have access to the selected item(s), or they no longer exist.",
          type: NotificationTypes.ERROR,
        });
        return;
      }

      setTrashIds(ids);
      setDeleteSummary(summary);
      setShowTrashMapLayerModal(false);
      setShowTrashSummaryModal(true);
    },
    [getDeleteSummary],
  );

  const handleTrashSummaryConfirm = useCallback(async () => {
    // Close summary before any network work so Spinner never stacks on a modal.
    setShowTrashSummaryModal(false);
    hideOverlay();

    // Map usage was resolved by the summary — no second round trip needed.
    if (deleteSummary?.mapUsage.hasMapUsage) {
      setShowTrashMapLayerModal(true);
      return;
    }

    await trashFiles(validTrashIds);
    setTrashIds([]);
    setDeleteSummary(null);
  }, [deleteSummary, validTrashIds, trashFiles]);

  const handleTrashMapLayerConfirm = useCallback(async () => {
    setShowTrashMapLayerModal(false);
    hideOverlay();
    await trashFiles(validTrashIds);
    setTrashIds([]);
    setDeleteSummary(null);
  }, [validTrashIds, trashFiles]);

  const { getAllMaps } = MapPortalService();
  const { data: allMapsData, isLoading: mapsLoading } = useQuery(getAllMaps());
  const mapOptions: CheckboxFilterListOption[] = useMemo(() => {
    const maps: any[] = Array.isArray(allMapsData) ? allMapsData : (allMapsData?.maps ?? []);
    return maps.map((map: any) => ({
      label: stripHtml(map.general?.mapTitle) || `${map.id}`,
      value: `${map.id}`,
    }));
  }, [allMapsData]);

  const { setFolderS3Key, setFolderId: setFolderIdContext, setFolderLocation: setFolderLocationContext } = useLegendForm();

  const useExternalFolderHandlers = Boolean(setFolderLocation || setFolderId);
  const setFolderLocationValue = useExternalFolderHandlers ? (setFolderLocation ?? (() => {})) : setFolderLocationContext;
  const setFolderIdValue = useExternalFolderHandlers ? (setFolderId ?? (() => {})) : setFolderIdContext;

  const updateUploadFolder = (folderId: string) => setUploadFolderId(folderId);
  const updateCurrentFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
    // Keep the default upload destination aligned with navigation. The upload
    // modal can still override this explicitly, while nested-folder uploads no
    // longer reuse the last destination selected in another folder.
    setUploadFolderId(folderId);
  };

  const handleFileOpen = (fileToOpen: any) => {
    if (fileToOpen?.isDir) {
      if (location.pathname === '/files/recents') return;
      setSearchInput('');
      updateCurrentFolder(fileToOpen.id);
      hidePreview();
      return;
    }

    const fileExtension =
      (fileToOpen?.name as string | undefined)?.split('.').pop()?.toLowerCase() ||
      (fileToOpen?.id as string | undefined)?.split('.').pop()?.toLowerCase();

    // No preview available for these -- Open is disabled in the details modal too.
    if (fileExtension && NO_PREVIEW_EXTS.has(fileExtension)) return;

    // Spatial files → temporary map preview route (US-109521)
    if (fileExtension && SPATIAL_PREVIEW_EXTS.has(fileExtension)) {
      window.open(`/files/preview/${encodeURIComponent(fileToOpen.id)}`, '_blank', 'noopener,noreferrer');
      return;
    }

    // Standard files → inline preview modal on this page.
    setPreviewFile(fileToOpen);
    setShowFilePreview(true);
  };

  // Location-click from FileDetailsModal: navigate to the folder shown in the
  // path. Recents has no folder-browsing UI, so it jumps to All Files instead;
  // All Files/Trash navigate within the current section.
  const handleNavigateToLocation = async (folderId: string) => {
    hidePreview();
    setSearchInput('');
    if (location.pathname === '/files/recents') {
      navigate('/files/all-files', { state: { openFolderId: folderId } });
      return;
    }
    await updateMapping();
    updateCurrentFolder(folderId);
  };

  // S3 key prefix for the upload target folder (id chain), matching FolderSelectorTree.
  const uploadFolderS3Key = useMemo(() => {
    if (!uploadFolderId || uploadFolderId === rootFolderId) return rootFolderId;

    const chain: string[] = [];
    let cursor: string | undefined = uploadFolderId;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      chain.unshift(cursor);
      const node: NullableFileNode = fileMap[cursor];
      cursor = node?.parentId;
    }
    // Ensure the chain starts at root (FolderSelectorTree uses [rootId, ...chain]).
    if (chain[0] !== rootFolderId) chain.unshift(rootFolderId);
    return chain.join('/');
  }, [uploadFolderId, rootFolderId, fileMap]);

  const { selectedFiles, uploadProgress, reuploadProgress, isUploading, cancelUpload, handleFileChange, handleFileUpload } = useFileUpload(
    uploadFolderId,
    updateMapping,
    uploadFolderS3Key,
  );

  const { handleBulkDownload } = useFileDownload();

  const handleFileSelection = (files: FileNode[]) => setChonkySelectedFiles(files);

  // Names of the folders directly inside the current directory. Derived from
  // childrenIds (same source the views render) so duplicate detection and
  // default-name generation stay in sync with what the user sees.
  const getCurrentFolderNames = useCallback(() => {
    const currentFolder = fileMap[currentFolderId];
    return (currentFolder?.childrenIds || [])
      .map((childId: string) => fileMap[childId])
      .filter((file) => file?.isDir)
      .map((file) => file?.name || '');
  }, [fileMap, currentFolderId]);

  // Sibling names of the same type (folders share a namespace with folders,
  // files with files) -- a folder and a file may share a name since the icon
  // disambiguates them. Excludes the item being renamed.
  const getSiblingNames = useCallback(
    (excludeId: string, isDir?: boolean) => {
      const currentFolder = fileMap[currentFolderId];
      return (currentFolder?.childrenIds || [])
        .filter((childId: string) => childId !== excludeId)
        .map((childId: string) => fileMap[childId])
        .filter((file) => Boolean(file?.isDir) === Boolean(isDir))
        .map((file) => file?.name || '');
    },
    [fileMap, currentFolderId],
  );

  const handleCommitName = useCallback(
    async (file: FileNode, value: string): Promise<string | void> => {
      if (renamingRef.current) return;

      const trimmed = value.trim();

      // Unchanged (empty is blocked inline before commit).
      if (!trimmed || trimmed === file.name) {
        setEditingFileId(null);
        return;
      }

      if (getSiblingNames(file.id, file.isDir).includes(trimmed)) {
        // Keep edit mode open; InlineRenameInput shows this as inline error.
        return DUPLICATE_NAME_MESSAGE;
      }

      renamingRef.current = true;
      // Exit edit mode immediately; renameFile applies an optimistic name update.
      setEditingFileId(null);

      const result = await renameFile(trimmed, file.id);
      renamingRef.current = false;

      if (!result.ok) {
        // Server-supplied reason (name conflict, reserved name, invalid
        // characters, length) rather than a generic failure string.
        Notification({
          message: result.message,
          type: NotificationTypes.ERROR,
        });
      }
    },
    [getSiblingNames, renameFile],
  );

  // "New Folder", then "New Folder (1)", "New Folder (2)", ... skipping any
  // name already taken in the current directory.
  const generateDefaultFolderName = (existing: string[]) => {
    const base = 'New Folder';
    if (!existing.includes(base)) return base;
    let index = 1;
    while (existing.includes(`${base} (${index})`)) index += 1;
    return `${base} (${index})`;
  };

  // Reached through fileManagerActionsRef rather than the action dispatcher.
  const startCreateFolder = useCallback(() => {
    if (!canManageFiles) return;
    submittingRef.current = false;
    setNewFolderName(generateDefaultFolderName(getCurrentFolderNames()));
    setCreatingFolder(true);
  }, [canManageFiles, getCurrentFolderNames]);

  const onCommitNewFolder = useCallback(
    async (name: string) => {
      // Ignore re-entrant commits (double-Enter, or the blur that fires when a
      // successful commit unmounts the temp tile).
      if (submittingRef.current) return;
      const trimmedName = name.trim();
      if (!trimmedName) {
        Notification({
          message: 'Folder name is required.',
          type: NotificationTypes.ERROR,
        });
        return;
      }

      if (getCurrentFolderNames().includes(trimmedName)) {
        Notification({
          message: 'A folder with this name already exists.',
          type: NotificationTypes.ERROR,
        });
        return;
      }

      submittingRef.current = true;
      setSubmittingFolder(true);

      // createFolder resolves to the created folder on success and to undefined
      // on failure (it swallows its own error), so a falsy result means the
      // create failed -- keep the temp tile and leave the directory unchanged.
      const created = await createFolder(trimmedName, currentFolderId);
      if (!created) {
        submittingRef.current = false;
        setSubmittingFolder(false);
        // Preserve what the user typed: the input is uncontrolled and remounts
        // from newFolderName when submitting flips back to false.
        setNewFolderName(trimmedName);
        Notification({
          message: 'Unable to create folder. Please try again.',
          type: NotificationTypes.ERROR,
        });
        return;
      }

      // Success: leave submittingRef set so the trailing unmount blur is ignored
      // (a fresh startCreateFolder resets it). Close out the temp tile.
      setSubmittingFolder(false);
      setCreatingFolder(false);
    },
    [getCurrentFolderNames, currentFolderId, createFolder],
  );

  const onCancelNewFolder = useCallback(() => {
    setCreatingFolder(false);
  }, []);

  // Rename never reaches dispatchFileAction -- dispatchRowAction intercepts it into
  // inline edit -- so it needs its own gate.
  const handleRequestRename = useCallback(
    (file: FileNode) => {
      if (!canAccess(fileActionAccess(FileAction.RENAME_FILE))) return;
      setEditingFileId(file.id);
    },
    [canAccess],
  );

  const handleCancelRename = useCallback(() => setEditingFileId(null), []);

  const handleFileAction = useFileActionHandler(
    setCurrentFolderId,
    handleBulkDownload,
    showReupload,
    showPreview,
    showMove,
    showTrashConfirmation,
    showRestoreTrash,
    showDeleteConfirmation,
    showPermissions,
    handleFileOpen,
    createImageLayer,
    removeImageLayer,
    handleFileSelection,
  );

  const fileActions = useMemo(() => {
    const commonActions = [
      {
        id: FileAction.PREVIEW_FILE,
        requiresSelection: true,
        button: { name: 'Preview', toolbar: false, contextMenu: true, icon: ChonkyIconName.text },
      },
      {
        id: FileAction.DOWNLOAD_FILES,
        requiresSelection: true,
        button: { name: 'Download', toolbar: false, contextMenu: true, icon: ChonkyIconName.download },
      },
      {
        id: FileAction.RENAME_FILE,
        requiresSelection: true,
        button: { name: 'Rename', toolbar: false, contextMenu: true, icon: ChonkyIconName.text },
      },
      { id: FileAction.MOVE_FILE, requiresSelection: true, button: { name: 'Move', toolbar: false, contextMenu: true, icon: ChonkyIconName.text } },
      {
        id: FileAction.DELETE_FILES,
        requiresSelection: true,
        button: { name: 'Delete', toolbar: false, contextMenu: true, icon: ChonkyIconName.trash },
      },
    ];

    const allowed = <T extends { id: string }>(actions: T[]) => actions.filter((action) => canAccess(fileActionAccess(action.id)));

    let homeActions = [...commonActions];
    // US-109520: Preview is not available for folders.
    let folderActions = commonActions.filter((a: any) => a?.id !== FileAction.PREVIEW_FILE);

    let allFileActions = [
      ...(canManageFiles ? [ChonkyActions.CreateFolder] : []),
      ...commonActions,
      {
        id: FileAction.REUPLOAD_FILE,
        requiresSelection: true,
        button: { name: 'Reupload File', toolbar: false, contextMenu: true, icon: ChonkyIconName.upload },
      },
    ];

    // `toolbar` is dropped here (was `true`) -- <FileToolbar> is never rendered
    // below (only <FileNavbar> is), so it had no effect; same is true of every
    // other `toolbar: false` in this fileActions definition (TODO: sweep those too).
    let trashActions = [
      {
        id: FileAction.RESTORE_FILE,
        requiresSelection: true,
        button: { name: 'Restore', tooltip: 'Restore selected file', contextMenu: true, icon: ChonkyIconName.loading },
      },
      {
        id: FileAction.PERMANENTLY_DELETE_FILE,
        requiresSelection: true,
        button: { name: 'Delete Permanently', tooltip: 'Permanently delete selected file', contextMenu: true, icon: ChonkyIconName.trash },
      },
    ];

    if (location.pathname === '/files/trash') return allowed(trashActions);
    else if (chonkySelectedFiles?.length > 0 && chonkySelectedFiles[0].isDir) return allowed(folderActions);
    else if (location.pathname === '/files/all-files') return allowed(allFileActions);
    else if (location.pathname === '/files/recents') return allowed(homeActions);
  }, [location.pathname, canAccess, canManageFiles, chonkySelectedFiles]);

  const thumbnailGenerator = useCallback((file: FileNode) => (file.thumbnailUrl ? `https://chonky.io${file.thumbnailUrl}` : null), []);

  const files = useMemo(() => {
    const currentFolder = fileMap[currentFolderId];
    updateLastAccessTime();
    const childrenIds = currentFolder?.childrenIds || [];
    return childrenIds.map((fileId: string) => fileMap[fileId]);
  }, [currentFolderId, fileMap]);

  const sortedFiles = useMemo(() => {
    const folders = files.filter((f) => f?.isDir).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    const regularFiles = files.filter((f) => !f?.isDir).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    return [...folders, ...regularFiles];
  }, [files]);

  // The root always renders as ROOT_DISPLAY_NAME, and an unpopulated fileMap
  // still yields a root crumb -- Chonky labels a missing node "Loading...".
  const folderChain = useMemo(() => {
    if (!rootFolderId) return [];

    const rootCrumb: FileNode = { id: rootFolderId, name: ROOT_DISPLAY_NAME, isDir: true };
    const currentFolder = fileMap[currentFolderId];
    if (!currentFolder || currentFolderId === rootFolderId) return [rootCrumb];

    const chain: FileNode[] = [currentFolder];
    let parentId = currentFolder.parentId;
    while (parentId && parentId !== rootFolderId) {
      const parentFile = fileMap[parentId];
      if (!parentFile) break;
      chain.unshift(parentFile);
      parentId = parentFile.parentId;
    }
    return [rootCrumb, ...chain];
  }, [currentFolderId, rootFolderId, fileMap]);

  // Was previously an inline prop on <FullFileBrowser onFileAction={...}>.
  // Pulled out unchanged so <CustomFileList> can dispatch through the exact
  // same path (handleFileAction + the folder-selection side effect below)
  // for checkbox clicks and 3-dot-menu actions, not just for whatever
  // FullFileBrowser used to fire internally.
  const dispatchFileAction = useCallback(
    (d: ChonkyFileActionData) => {
      // Backstop for the menus filtered below -- a denial here means an action the
      // role can't see was dispatched anyway, so drop it silently.
      if (!canAccess(fileActionAccess(d.id))) return;
      handleFileAction(d, fileMap);
      if (d.id === ChonkyActions.ChangeSelection.id) {
        d.state.selectedFilesForAction.forEach((file) => {
          if (file.isDir) {
            setFolderLocationValue(file.name);
            setFolderIdValue(file?.id);
            const folderPath = folderChain
              .map((folder) => folder?.id)
              .concat(file.id)
              .join('/');
            setFolderS3Key?.(folderPath);
          }
        });
      }
    },
    [canAccess, handleFileAction, fileMap, folderChain, setFolderLocationValue, setFolderIdValue, setFolderS3Key],
  );

  // Location-click navigation (Recents -> All Files) passes the target folder
  // id via route state, since a pathname change remounts this component.
  const pendingOpenFolderId = (location.state as { openFolderId?: string } | null)?.openFolderId;
  useEffect(() => {
    if (!pendingOpenFolderId) setCurrentFolderId('');
  }, [location.pathname]);

  // Apply the pending folder once its fileMap entry has loaded, then clear the state.
  useEffect(() => {
    if (pendingOpenFolderId && fileMap[pendingOpenFolderId]) {
      setCurrentFolderId(pendingOpenFolderId);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [fileMap, pendingOpenFolderId]);

  // Selection is tracked in two places (our own `selection` state and chonky's
  // internal per-instance store) and neither clears on its own -- reset both
  // whenever the section (route) or the current folder changes, so a
  // previously-selected id can't still read as checked in a different list.
  useEffect(() => {
    setSelection([]);
    setChonkySelectedFiles([]);
    fileBrowserRef.current
      ?.requestFileAction(ChonkyActions.ClearSelection, undefined)
      ?.catch((error) => console.error('Failed to clear chonky selection: ', error));
  }, [location.pathname, currentFolderId]);

  useEffect(() => {
    if (fileManagerActionsRef) {
      fileManagerActionsRef.current = {
        startCreateFolder,
        openUpload: showAddFiles,
      };
    }
  }, [startCreateFolder, showAddFiles, fileManagerActionsRef]);

  // Mirror the inline-creation state up so the footer can show the active
  // add-folder icon (the ref registry above is not reactive).
  useEffect(() => {
    props.onCreatingFolderChange?.(creatingFolder);
  }, [creatingFolder, props.onCreatingFolderChange]);

  const [dragging, setDragging] = useState(false);

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canManageFiles || (showAddFilesModal && showReuploadModal)) setDragging(false);
    else setDragging(true);
  };
  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const handleDrop = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canManageFiles) return;
    const droppedFiles = Array.from(e.dataTransfer.files) as File[];
    if (droppedFiles.length > 0 && dragging && !showAddFilesModal) {
      const confirmUpload = window.confirm(`Upload ${droppedFiles.length} file(s) to the current folder?`);
      if (confirmUpload) {
        const dataTransfer = new DataTransfer();
        droppedFiles.forEach((file) => {
          if (file) dataTransfer.items.add(file);
        });
        const event = { target: { files: dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>;
        updateUploadFolder(currentFolderId);
        handleFileChange(event, true);
      }
    }
    setDragging(false);
  };

  return (
    <>
      <div
        className="file-manager__content"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        {applyOverlay && <div className="overlay"></div>}
        <UploadModal
          show={showAddFilesModal}
          hide={hideAddFiles}
          rootFolderId={rootFolderId}
          handleFileChange={handleFileChange}
          handleFileUpload={handleFileUpload}
          createNewFolder={createFolder}
          uploadFolder={uploadFolderId}
          updateUploadFolder={updateUploadFolder}
          selectedFiles={selectedFiles}
          currentFolderID={currentFolderId}
          isUploading={isUploading}
        />
        <ReuploadModal
          show={showReuploadModal}
          hide={hideReupload}
          selectedFiles={selectedFiles}
          reuploadFiles={selectedReuploadFiles}
          handleFileChange={handleFileChange}
          handleFileUpload={handleFileUpload}
          isUploading={isUploading}
        />
        <FileDetailsModal
          show={showPreviewModal}
          hide={hidePreview}
          selectedFile={selectedPreviewFile}
          retrieveFileLocation={getFileLocation}
          handleFileOpen={handleFileOpen}
          onNavigateToLocation={handleNavigateToLocation}
        />
        <PreviewFileModal show={showFilePreview} hide={hideFilePreview} selectedFile={previewFile} />
        <MoveModal
          open={showMoveModal}
          onClose={hideMove}
          files={selectedMoveFiles}
          rootFolderId={rootFolderId}
          currentFolderId={currentFolderId}
          moveFiles={moveFiles}
        />
        <RestoreTrashModal
          show={showRestoreTrashModal}
          hide={hideRestoreTrash}
          selectedRestoreFiles={selectedRestoreFiles}
          restoreFiles={restoreFiles}
        />
        <FilePermissionsModal
          open={showPermissionsModal}
          onClose={hidePermissions}
          file={selectedPermissionsFile}
          retrieveFileLocation={getFileLocation}
        />
        <CustomWarning
          key="trash-delete-confirm"
          open={showTrashSummaryModal}
          onCancel={abortTrashFlow}
          onConfirm={() => {
            void handleTrashSummaryConfirm();
          }}
          isLoading={isTrashing}
          message={trashSummaryMessage}
          cancelText="Cancel"
          confirmText="Delete"
          variant="dark"
          showIcon={false}
          size="large"
        />
        <CustomWarning
          key="trash-map-layer-confirm"
          open={showTrashMapLayerModal}
          onCancel={abortTrashFlow}
          onConfirm={() => {
            void handleTrashMapLayerConfirm();
          }}
          isLoading={isTrashing}
          message={trashMapLayerMessage}
          cancelText="Cancel"
          confirmText="Delete"
          variant="dark"
          showIcon
          size="large"
        />
        <CustomWarning
          open={showDeleteConfirmationModal}
          onCancel={hideDeleteConfirmation}
          onConfirm={async () => {
            await deleteFiles(selectedDeleteFiles!);
            hideDeleteConfirmation();
          }}
          isLoading={isDeleting}
          message={
            (selectedDeleteFiles?.length ?? 0) > 1
              ? 'Are you sure you want to permanently delete the selected items? This action cannot be undone.'
              : selectedDeleteFiles?.[0]?.isDir
                ? 'Are you sure you want to permanently delete this folder and all of its contents? This action cannot be undone.'
                : 'Are you sure you want to permanently delete this item? This action cannot be undone.'
          }
          cancelText="Cancel"
          confirmText="Delete"
          variant="dark"
        />
        <UploadProgressModal uploadProgress={uploadProgress ?? reuploadProgress} onCancel={cancelUpload} />

        <div className="toolbar__container">
          <div className="filter__container">
            <ConditionalRender visible={fileListViewType === 'tile'}>
              <SelectAllCheckbox files={sortedFiles} selection={selection} setSelection={setSelection} onFileAction={dispatchFileAction} />
            </ConditionalRender>
            <Dropdown
              dropdownRender={() => (
                <FilterPanel
                  selection={draftSelection}
                  onChange={handleDraftSelectionChange}
                  onApply={handleApplyFilters}
                  onClearAll={handleClearAllFilters}
                  mapOptions={mapOptions}
                  isApplying={isFilterApplyLoading || mapsLoading}
                />
              )}
              trigger={['click']}
              placement="bottomLeft"
              open={filterDropdownOpen}
              onOpenChange={handleFilterDropdownOpenChange}
            >
              <Button title="Advanced Filter Options" className={`filter__button${isFilterActive ? ' filter__button--active' : ''}`} type="default">
                FILTERS <AiFillCaretDown />
              </Button>
            </Dropdown>
            <SearchBar value={searchInput} onChange={setSearchInput} />
          </div>
          <div aria-label="view-selector" className="custom_segmented">
            <div
              aria-label="list-view"
              className={`view_option ${fileListViewType === 'list' ? 'option--active' : ''}`}
              onClick={() => setFileListViewType('list')}
            >
              <svg width="35" height="28" viewBox="0 0 35 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_34_2272)">
                  <path
                    d="M3.6399 27.2C5.42931 27.2 6.8799 25.7494 6.8799 23.96C6.8799 22.1706 5.42931 20.72 3.6399 20.72C1.8505 20.72 0.399902 22.1706 0.399902 23.96C0.399902 25.7494 1.8505 27.2 3.6399 27.2Z"
                    fill="#D3D3D3"
                    stroke="#D3D3D3"
                    stroke-width="0.8"
                    stroke-miterlimit="10"
                  />
                  <path
                    d="M3.6399 17.0398C5.42931 17.0398 6.8799 15.5892 6.8799 13.7998C6.8799 12.0104 5.42931 10.5598 3.6399 10.5598C1.8505 10.5598 0.399902 12.0104 0.399902 13.7998C0.399902 15.5892 1.8505 17.0398 3.6399 17.0398Z"
                    fill="#D3D3D3"
                    stroke="#D3D3D3"
                    stroke-width="0.8"
                    stroke-miterlimit="10"
                  />
                  <path
                    d="M3.6399 6.8799C5.42931 6.8799 6.8799 5.42931 6.8799 3.6399C6.8799 1.8505 5.42931 0.399902 3.6399 0.399902C1.8505 0.399902 0.399902 1.8505 0.399902 3.6399C0.399902 5.42931 1.8505 6.8799 3.6399 6.8799Z"
                    fill="#D3D3D3"
                    stroke="#D3D3D3"
                    stroke-width="0.8"
                    stroke-miterlimit="10"
                  />
                  <path d="M10.99 3.63989H34.93" stroke="white" stroke-miterlimit="10" />
                  <path d="M10.99 13.7998H34.84" stroke="white" stroke-miterlimit="10" />
                  <path d="M10.99 23.96H34.84" stroke="white" stroke-miterlimit="10" />
                </g>
                <defs>
                  <clipPath id="clip0_34_2272">
                    <rect width="34.93" height="27.6" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div
              aria-label="tile-view"
              className={`view_option ${fileListViewType === 'tile' ? 'option--active' : ''}`}
              onClick={() => setFileListViewType('tile')}
            >
              <svg width="36" height="30" viewBox="0 0 36 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect y="11" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect y="22" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="13" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="13" y="11" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="13" y="22" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="26" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="26" y="11" width="10" height="8" rx="2" fill="#D9D9D9" />
                <rect x="26" y="22" width="10" height="8" rx="2" fill="#D9D9D9" />
              </svg>
            </div>
          </div>
        </div>

        <Spin
          spinning={isMappingLoading || searchLoading || isFilterApplyLoading || isTrashing || isMoving}
          size="large"
          className="search-spinner"
          wrapperClassName="file-manager__spin"
        >
          <div className="file-manager__browser">
            <FileBrowser
              ref={fileBrowserRef}
              instanceId="vfs-browser"
              files={sortedFiles}
              defaultFileViewActionId={ChonkyActions.EnableListView.id}
              folderChain={folderChain}
              fileActions={fileActions}
              darkMode
              disableSelection
              onFileAction={dispatchFileAction}
              thumbnailGenerator={thumbnailGenerator}
              {...props}
            >
              <ConditionalRender visible={!isSearchActive && location.pathname !== '/files/recents'}>
                <FileNavbar />
              </ConditionalRender>
              {fileListViewType === 'tile' ? (
                <FileGridView
                  files={sortedFiles}
                  onFileAction={dispatchFileAction}
                  onOpenFolder={handleFileOpen}
                  onPreviewFile={(f) => showPreview([f])}
                  selection={selection}
                  setSelection={setSelection}
                  editingId={editingFileId}
                  isTrash={location.pathname === '/files/trash'}
                  onRequestRename={handleRequestRename}
                  onCommitName={handleCommitName}
                  onCancelRename={handleCancelRename}
                  creatingFolder={creatingFolder}
                  newFolderDefaultName={newFolderName}
                  submitting={submittingFolder}
                  onCommitNewFolder={onCommitNewFolder}
                  onCancelNewFolder={onCancelNewFolder}
                  searchTerm={searchInput}
                  isSearchActive={isSearchActive}
                  isFilterActive={isFilterActive}
                  onSelectSearchResult={onSelectSearchResult}
                />
              ) : (
                <CustomFileList
                  files={sortedFiles}
                  onFileAction={dispatchFileAction}
                  onOpenFolder={handleFileOpen}
                  onPreviewFile={(f) => showPreview([f])}
                  selection={selection}
                  setSelection={setSelection}
                  editingId={editingFileId}
                  isTrash={location.pathname === '/files/trash'}
                  onRequestRename={handleRequestRename}
                  onCommitName={handleCommitName}
                  onCancelRename={handleCancelRename}
                  creatingFolder={creatingFolder}
                  newFolderDefaultName={newFolderName}
                  submitting={submittingFolder}
                  onCommitNewFolder={onCommitNewFolder}
                  onCancelNewFolder={onCancelNewFolder}
                  searchTerm={searchInput}
                  isSearchActive={isSearchActive}
                  isFilterActive={isFilterActive}
                  onSelectSearchResult={onSelectSearchResult}
                />
              )}
            </FileBrowser>
          </div>
        </Spin>
      </div>
    </>
  );
});
